import React from 'react';
import { useIntl } from 'react-intl';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Info } from '~/eds/src/modules/Info/Info';
import { QueryFiltersFormField } from '~/features/advanced-search/query-filters/QueryFiltersFormField';
import { SearchQuery } from '~/features/advanced-search/types';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientId } from '~/hooks/useClientId';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';

import { usePollModelVersionProgress } from './hooks/usePollModelVersionProgress';
import { useGetLibraryModelVersion } from './library/hooks';
import {
  testIsActivePublishStatus,
  testIsPublishingStatus,
} from './utils/status';

interface Props {
  modelId: string;
  version: number;
  enableTitle?: boolean;
}

export const ModelVersionInfo = ({ enableTitle, modelId, version }: Props) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const clientId = useClientId();

  const enablePrebuiltModelLibrary = useFlag(
    FlagType.DocumentXRayPrebuiltModels,
  );
  const enablePrebuiltModelsAdmin = useFlag(
    FlagType.DocumentXRayPrebuiltModelsAdmin,
  );

  const {
    modelVersion,
    modelProgress,
    isErrorModelVersion,
    isLoadingModelProgress,
    isLoadingModelVersion,
  } = usePollModelVersionProgress({
    modelId,
    version,
  });

  const { data: createdByUser, isFetching: isLoadingCreatedByUser } =
    api.endpoints.resolveUser.useQuery(
      {
        // @ts-expect-error -- rtkq query is skipped correctly
        id: modelVersion?.createdBy,
        params: { clientId },
      },
      {
        skip: !modelVersion,
      },
    );

  const { docsInScope, docsProcessed, publishStatus } = modelProgress;

  const { model: libraryModel, isLoading: isLoadingLibraryModel } =
    useGetLibraryModelVersion({
      libraryModelId: modelVersion?.libraryModelId,
      libraryModelVersion: modelVersion?.libraryModelVersion,
    });

  return (
    <ContentContainer
      loadingContent={{
        isLoading: isLoadingModelVersion || isLoadingLibraryModel,
        message: intl.formatMessage({
          id: 'ModelVersionInfo.loadingMessage',
          defaultMessage: 'Loading model details…',
          description: 'Loading message for model version details',
        }),
      }}
      placeholderContent={
        isErrorModelVersion
          ? {
              message: intl.formatMessage({
                id: 'ModelVersionInfo.errorMessage',
                defaultMessage:
                  'Something went wrong, please re-open the details panel',
                description: 'Error message when model version loading fails',
              }),
            }
          : undefined
      }
    >
      {modelVersion && (
        <Info
          sections={[
            {
              title: enableTitle
                ? intl.formatMessage({
                    id: 'ModelVersionInfo.generalInformation',
                    defaultMessage: 'General Information',
                    description: 'Section title for general model information',
                  })
                : '',
              infos: [
                ...(isLoadingModelProgress ||
                testIsActivePublishStatus(publishStatus)
                  ? [
                      {
                        title: intl.formatMessage({
                          id: 'ModelVersionInfo.percentageCompletedTitle',
                          defaultMessage: 'Percentage Completed',
                          description: 'Label for model completion percentage',
                        }),
                        type: 'progress' as const,
                        props: {
                          isLoading:
                            isLoadingModelProgress ||
                            testIsPublishingStatus(publishStatus),
                          max: docsInScope,
                          name: 'publish progress',
                          status: 'active' as const,
                          statsLabel: {
                            formatLabel: (value: number, max: number) => {
                              return intl.formatMessage(
                                {
                                  id: 'ModelVersionInfo.percentageCompletedStats',
                                  defaultMessage:
                                    '{percent} ({value} of {max} Documents)',
                                },
                                {
                                  percent:
                                    max > 0
                                      ? `${formatNumber(value / max, {
                                          decimal: 1,
                                          style: 'percent',
                                        })}`
                                      : `–%`,
                                  value: `${formatNumber(value)}`,
                                  max: `${formatNumber(max)}`,
                                },
                              );
                            },
                            position: 'bottom' as const,
                          },
                          value: docsProcessed,
                        },
                      },
                    ]
                  : []),
                {
                  title: intl.formatMessage({
                    id: 'ModelVersionInfo.scope',
                    defaultMessage: 'Scope',
                    description: 'Label for model scope section',
                  }),
                  type: 'content' as const,
                  props: {
                    children: (
                      <QueryFiltersFormField
                        mode="preview"
                        readOnly
                        optional={false}
                        query={(modelVersion.scope as SearchQuery) ?? []}
                        onQueryChange={() => {}}
                        title={intl.formatMessage({
                          id: 'ModelVersionInfo.currentScope',
                          defaultMessage: 'Current Scope',
                          description: 'Title for current model scope display',
                        })}
                      />
                    ),
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'ModelVersionInfo.documentsInScope',
                    defaultMessage: 'Documents in Scope',
                    description: 'Label for number of documents in scope',
                  }),
                  type: 'number' as const,
                  props: {
                    number: docsInScope,
                  },
                },
                {
                  title: intl.formatMessage({
                    id: 'ModelVersionInfo.userActivity',
                    defaultMessage: 'User activity',
                    description: 'Label for user activity section',
                  }),
                  type: 'user-activity' as const,
                  props: {
                    activity: intl.formatMessage({
                      id: 'ModelVersionInfo.modified',
                      defaultMessage: 'Modified',
                      description: 'Label for modification activity',
                    }),
                    date: modelVersion.createdDate,
                    user: createdByUser!,
                    format: 'short_datetime',
                  },
                  isLoading: isLoadingCreatedByUser,
                },
                ...(enablePrebuiltModelLibrary &&
                libraryModel &&
                !enablePrebuiltModelsAdmin
                  ? [
                      {
                        title: intl.formatMessage({
                          id: 'ModelVersionInfo.libraryModelInstructions',
                          defaultMessage: 'Library Model Instructions',
                          description: 'Label for library model instructions',
                        }),
                        type: 'textarea' as const,
                        props: {
                          readOnly: true,
                          name: 'libraryModelInstructions',
                          value: libraryModel.description,
                        },
                      },
                    ]
                  : []),
                {
                  title:
                    enablePrebuiltModelLibrary &&
                    libraryModel &&
                    !enablePrebuiltModelsAdmin
                      ? intl.formatMessage({
                          id: 'ModelVersionInfo.additionalInstructions',
                          defaultMessage: 'Additional Instructions',
                          description:
                            'Label for additional model instructions section',
                        })
                      : intl.formatMessage({
                          id: 'ModelVersionInfo.instructionsForModel',
                          defaultMessage: 'Instructions for the Model',
                          description: 'Label for model instructions section',
                        }),
                  type: 'textarea' as const,
                  props: {
                    readOnly: true,
                    name: 'instructions',
                    value: modelVersion.config.instructions ?? '',
                  },
                },
              ],
            },
          ]}
        />
      )}
    </ContentContainer>
  );
};
